import type { PluginSidebarProps } from "@d12labs/plugins/types";
import {
	BrandSquare,
	SidebarBrand,
	SidebarNavItem,
	SidebarSection,
	SidebarSubItem,
} from "@d12labs/ui/sidebar-kit";
import { useQuery } from "@tanstack/react-query";
import { Activity, Calendar, Home, Star, Upload, Users } from "lucide-react";
import { meetingPerfApi } from "./api";

/**
 * Contextual sidebar for the Meeting Performance plugin (the app shell renders
 * this when a `/meeting-perf` route is active). Workspace nav + a live "Tracked"
 * list of speakers; the back chevron in the header returns to the app launcher.
 */
export const pluginSidebar = ({ onBack, onNavigate }: PluginSidebarProps) => {
	const speakers = useQuery({
		queryKey: ["meeting-perf", "speakers"],
		queryFn: () => meetingPerfApi.listSpeakers(),
	});

	const tracked = (speakers.data ?? []).filter((s) => s.tracked);

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
			<SidebarBrand
				icon={<BrandSquare icon={Home} />}
				title="Meeting Perf"
				subtitle="Performance coaching"
				onBack={onBack}
			/>
			<div className="flex flex-col gap-1 px-2 py-2">
				<SidebarSection label="Workspace">
					<SidebarNavItem
						to="/meeting-perf"
						exact
						icon={Home}
						label="Dashboard"
						onNavigate={onNavigate}
					/>
					<SidebarNavItem
						to="/meeting-perf/upload"
						icon={Upload}
						label="New meeting"
						onNavigate={onNavigate}
					/>
					<SidebarNavItem
						to="/meeting-perf/meetings"
						icon={Calendar}
						label="Meetings"
						onNavigate={onNavigate}
					/>
					<SidebarNavItem
						to="/meeting-perf/speakers"
						icon={Users}
						label="Speakers"
						onNavigate={onNavigate}
					/>
					<SidebarNavItem
						to="/meeting-perf/activity"
						icon={Activity}
						label="Activity"
						onNavigate={onNavigate}
					/>
				</SidebarSection>

				{tracked.length > 0 ? (
					<SidebarSection label="Tracked">
						{tracked.map((s) => (
							<SidebarSubItem
								key={s.id}
								to={`/meeting-perf/speakers/${s.id}`}
								label={s.name}
								primary={s.isPrimary}
								onNavigate={onNavigate}
								trailing={
									s.tracked ? <Star className="h-3 w-3 fill-current text-foreground" /> : undefined
								}
							/>
						))}
					</SidebarSection>
				) : null}
			</div>
		</div>
	);
};
